import { useEffect, useMemo, useState } from 'react';
import { useChatStore } from '../../../../store/chatStore';
import { getRoomAttachments } from '../../../../api/chat';
import {
  mergeRoomAttachmentsDated,
  type DatedAttachment,
  type RoomAttachmentItem,
} from '../../../../lib/room-attachments';

/**
 * 방 전체 첨부(출처 메타 포함) 로딩 훅 (chat-media-date-groups — 2026-08-07).
 *
 * 기존 `ChatMediaPanel` 안에 인라인돼 있던 페이징 effect 와 **동일한 계약**이다 — 같은 상수
 * (PAGE_LIMIT 200 / MAX_PAGES 10), 같은 `cancelled` 가드, 같은 "조용한 catch → 스토어 분만으로 강등"
 * 폴백. 다른 점은 (a) `mergeRoomAttachmentsDated` 로 createdAt/senderId 를 살리고 (b) loading 상태를
 * 노출한다는 것뿐이다.
 *
 * ⚠ 기존 `ChatMediaPanel.tsx` 는 롤백 경로로 원본 그대로 남겨둔다 — 그래서 페이징 로직이 두 곳에
 * 존재한다. 의도적인 중복이다(회귀 0 우선).
 *
 * 서버 응답은 방을 열 때 한 번 받으므로 그 뒤 SSE 로 도착한 첨부는 스토어에만 있다. 둘을 합쳐야
 * "전체 대화 + 실시간" 이 모두 성립한다.
 */

/** 한 번에 훑을 메시지 수. 서버가 첨부 보유 메시지만 세므로 한 페이지로 대부분 끝난다. */
const PAGE_LIMIT = 200;
/** 방어적 상한 — 비정상적으로 첨부가 많은 방에서 무한 페이징을 막는다. */
const MAX_PAGES = 10;

export interface RoomAttachmentsState {
  items: DatedAttachment[];
  loading: boolean;
}

export function useRoomAttachmentsDated(roomId: string): RoomAttachmentsState {
  const messages = useChatStore((s) => s.messagesByRoom[roomId]);
  const [roomItems, setRoomItems] = useState<RoomAttachmentItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 방이 바뀌면 이전 결과를 즉시 버린다(다른 방 첨부 노출 방지).
  useEffect(() => {
    let cancelled = false;
    setRoomItems([]);
    setLoading(true);
    (async () => {
      const acc: RoomAttachmentItem[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const res = await getRoomAttachments(roomId, { cursor, limit: PAGE_LIMIT });
        if (cancelled) return;
        acc.push(...res.items);
        if (!res.nextCursor) break;
        cursor = res.nextCursor;
      }
      if (!cancelled) {
        setRoomItems(acc);
        setLoading(false);
      }
    })().catch(() => {
      // 조회 실패 시 스토어 분(로드된 창)만으로 계속 동작 — 기존 동작 유지.
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  const items = useMemo(() => mergeRoomAttachmentsDated(roomItems, messages), [roomItems, messages]);

  return { items, loading };
}
